import api from '../../../api/client';
import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Tabs,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  Table,
  Space,
  Popconfirm,
  message,
  Tag,
  Row,
  Col,
  Badge,
  Tooltip,
  Switch,
  Upload,
  Avatar,
  List,
  Divider,
  Drawer,
  Timeline,
  InputNumber,
  Checkbox,
  Dropdown,
  Menu,
  Typography,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  PaperClipOutlined,
  CommentOutlined,
  MoreOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import axios from "axios";
import { useSelector } from "react-redux";
import { CSS } from "@dnd-kit/utilities";
import { TaskDetailsDrawer } from "./TaskDetailsDrawer";
import { DndContext, closestCenter } from "@dnd-kit/core";

import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import config from "../../../config";

dayjs.extend(relativeTime);

const { TabPane } = Tabs;
const { TextArea } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Title } = Typography;

// ==================== Main Component ====================
const CalendarManagement = () => {
  const account = useSelector((state) => state.account);
  const [activeTab, setActiveTab] = useState("list");
  const [tasks, setTasks] = useState([]);
  const [events, setEvents] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [taskModalVisible, setTaskModalVisible] = useState(false);
  const [eventModalVisible, setEventModalVisible] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [taskForm] = Form.useForm();
  const [eventForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    assignee: null,
    status: null,
    priority: null,
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [customStatuses, setCustomStatuses] = useState([]);
  const [addStatusModalVisible, setAddStatusModalVisible] = useState(false);
  const [newStatusForm] = Form.useForm();
  const [calendarKey, setCalendarKey] = useState(0); // Force calendar refresh

  const environment =
    window.location.hostname === "localhost" ? "development" : "production";
  const apiUrl = config[environment].apiUrl;

  // Fetch data
  const fetchTasks = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.assignee) params.append("assignee", filters.assignee);
      if (filters.status) params.append("status", filters.status);
      if (filters.priority) params.append("priority", filters.priority);
      const { data } = await api.get(
        `/api/tasks?${params.toString()}`,
      );
      setTasks(data);
      setCalendarKey((prev) => prev + 1); // refresh calendar
    } catch (err) {
      message.error("Failed to fetch tasks");
    }
  }, [apiUrl, filters]);

  const fetchEvents = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/events`);
      setEvents(data);
      setCalendarKey((prev) => prev + 1);
    } catch (err) {
      message.error("Failed to fetch events");
    }
  }, [apiUrl]);

  const fetchHolidays = useCallback(async () => {
    try {
      const { data } = await api.get(
        `/api/holidays?year=${dayjs().year()}`,
      );
      setHolidays(data);
      setCalendarKey((prev) => prev + 1);
    } catch (err) {
      message.error("Failed to fetch holidays");
    }
  }, [apiUrl]);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/users-simple`);
      setUsers(data);
    } catch (err) {
      message.error("Failed to fetch users");
    }
  }, [apiUrl]);

  const fetchCustomStatuses = useCallback(async () => {
    try {
      const { data } = await api.get(
        `/api/task-statuses?user_id=${account.user.id}`,
      );
      setCustomStatuses(data);
    } catch (err) {
      console.error(err);
    }
  }, [apiUrl, account.user.id]);

  useEffect(() => {
    fetchTasks();
    fetchEvents();
    fetchHolidays();
    fetchUsers();
    fetchCustomStatuses();
  }, [fetchTasks, fetchEvents, fetchHolidays, fetchUsers, fetchCustomStatuses]);

  // After any data fetch that affects calendar, update key
  useEffect(() => {
    setCalendarKey((prev) => prev + 1);
  }, [tasks, events, holidays]);
  // Task CRUD
  const handleTaskSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        due_date: values.due_date?.format("YYYY-MM-DD HH:mm:ss"),
        start_date: values.start_date?.format("YYYY-MM-DD HH:mm:ss"),
        created_by: account.user.id,
      };
      if (editingTask) {
        await api.put(`/api/tasks/${editingTask.id}`, payload);
        message.success("Task updated");
      } else {
        await api.post(`/api/tasks`, payload);
        message.success("Task created");
      }
      setTaskModalVisible(false);
      taskForm.resetFields();
      setEditingTask(null);
      fetchTasks();
    } catch (err) {
      message.error("Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const updateTask = async (id, updates) => {
    try {
      await api.put(`/api/tasks/${id}`, updates);
      fetchTasks();
      if (selectedTaskId === id) setSelectedTaskId(id);
      message.success("Task updated");
    } catch (err) {
      message.error("Update failed");
    }
  };

  const deleteTask = async (id) => {
    try {
      await api.delete(`/api/tasks/${id}`);
      message.success("Task deleted");
      fetchTasks();
      if (selectedTaskId === id) setDrawerVisible(false);
    } catch (err) {
      message.error("Delete failed");
    }
  };

  const bulkUpdate = async (type, value) => {
    if (selectedRowKeys.length === 0) return;
    try {
      await api.post(`/api/tasks/bulk-update`, {
        ids: selectedRowKeys,
        type,
        value,
      });
      message.success(`Updated ${selectedRowKeys.length} tasks`);
      setSelectedRowKeys([]);
      fetchTasks();
    } catch (err) {
      message.error("Bulk update failed");
    }
  };

  const addCustomStatus = async (values) => {
    try {
      await api.post(`/api/task-statuses`, {
        ...values,
        user_id: account.user.id,
      });
      message.success("Status added");
      setAddStatusModalVisible(false);
      newStatusForm.resetFields();
      fetchCustomStatuses();
    } catch (err) {
      message.error("Failed to add status");
    }
  };

  // Table columns
  const columns = [
    { title: "Title", dataIndex: "title", key: "title", sorter: true },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => (
        <Select
          value={status}
          onChange={(val) => updateTask(record.id, { status: val })}
          style={{ width: 120 }}
        >
          <Option value="todo">To Do</Option>
          <Option value="in_progress">In Progress</Option>
          <Option value="review">Review</Option>
          <Option value="done">Done</Option>
        </Select>
      ),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      render: (p) => (
        <Tag color={p === "urgent" ? "red" : p === "high" ? "orange" : "green"}>
          {p}
        </Tag>
      ),
    },
    {
      title: "Due Date",
      dataIndex: "due_date",
      key: "due_date",
      render: (d) => dayjs(d).format("DD MMM YYYY"),
    },
    { title: "Assignee", dataIndex: "firstname", key: "assignee" },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => {
                setEditingTask(record);
                taskForm.setFieldsValue({
                  ...record,
                  due_date: dayjs(record.due_date),
                  start_date: record.start_date
                    ? dayjs(record.start_date)
                    : null,
                });
                setTaskModalVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title="View Details">
            <Button
              icon={<CommentOutlined />}
              size="small"
              onClick={() => {
                setSelectedTaskId(record.id);
                setDrawerVisible(true);
              }}
            />
          </Tooltip>
          <Popconfirm title="Delete?" onConfirm={() => deleteTask(record.id)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE,
    ],
  };

  // Kanban Board
  const TaskCard = ({ task, onClick }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id: task.id });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      marginBottom: 8,
      cursor: "pointer",
    };
    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        onClick={onClick}
      >
        <Card size="small" hoverable>
          <Tag
            color={
              task.priority === "urgent"
                ? "red"
                : task.priority === "high"
                ? "orange"
                : "green"
            }
          >
            {task.priority}
          </Tag>
          <div>
            <strong>{task.title}</strong>
          </div>
          <div>
            <small>Due: {dayjs(task.due_date).format("DD MMM")}</small>
          </div>
          <div>
            <Avatar
              size="small"
              src={`https://ui-avatars.com/api/?name=${task.firstname || ""}`}
            />{" "}
            {task.firstname || "Unassigned"}
          </div>
        </Card>
      </div>
    );
  };

  const TaskBoard = () => {
    const defaultStatuses = [
      { id: "todo", name: "To Do", color: "#faad14" },
      { id: "in_progress", name: "In Progress", color: "#1890ff" },
      { id: "review", name: "Review", color: "#722ed1" },
      { id: "done", name: "Done", color: "#52c41a" },
    ];
    const allStatuses = [...defaultStatuses, ...customStatuses];
    const tasksByStatus = tasks.reduce(
      (acc, t) => ({ ...acc, [t.status]: [...(acc[t.status] || []), t] }),
      {},
    );

    return (
      <div>
        <Button
          onClick={() => setAddStatusModalVisible(true)}
          style={{ marginBottom: 16 }}
        >
          + Add Custom Status Column
        </Button>
        <div style={{ display: "flex", gap: 16, overflowX: "auto" }}>
          {allStatuses.map((s) => (
            <Card key={s.id} style={{ flex: 1, minWidth: 280 }}>
              <Title level={5} style={{ color: s.color }}>
                {s.name} ({tasksByStatus[s.id]?.length || 0})
              </Title>
              <DndContext
                collisionDetection={closestCenter}
                onDragEnd={({ active, over }) => {
                  if (!over) return;
                  const taskId = Number(active.id);
                  const newStatus = over.id;
                  updateTask(taskId, { status: newStatus });
                }}
              >
                <div style={{ minHeight: 400 }}>
                  {(tasksByStatus[s.id] || []).map((t) => (
                    <TaskCard
                      key={t.id}
                      task={t}
                      onClick={() => {
                        setSelectedTaskId(t.id);
                        setDrawerVisible(true);
                      }}
                    />
                  ))}
                </div>
              </DndContext>
            </Card>
          ))}
        </div>
      </div>
    );
  };

  // Calendar events
  const calendarEvents = [
    ...tasks.map((t) => ({
      id: `task_${t.id}`,
      title: t.title,
      start: t.due_date,
      end: t.due_date,
      color:
        t.priority === "urgent"
          ? "#f5222d"
          : t.priority === "high"
          ? "#fa8c16"
          : t.priority === "medium"
          ? "#faad14"
          : "#52c41a",
      extendedProps: { type: "task", data: t },
    })),
    ...events.map((e) => ({
      id: `event_${e.id}`,
      title: e.title,
      start: e.start_date,
      end: e.end_date,
      color: e.color || "#1890ff",
      extendedProps: { type: "event", data: e },
    })),
    ...holidays.map((h) => ({
      id: `hol_${h.id}`,
      title: h.name,
      start: h.date,
      end: h.date,
      color: "#8c8c8c",
      display: "background",
      extendedProps: { type: "holiday", data: h },
    })),
  ];

  return (
    <Card title="Workspace">
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="List" key="list">
          <div
            style={{
              marginBottom: 16,
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              justifyContent: "space-between",
            }}
          >
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingTask(null);
                  taskForm.resetFields();
                  setTaskModalVisible(true);
                }}
              >
                New Task
              </Button>
              <Button
                icon={<CalendarOutlined />}
                onClick={() => {
                  setEditingEvent(null);
                  eventForm.resetFields();
                  setEventModalVisible(true);
                }}
              >
                New Event
              </Button>
              <Dropdown
                overlay={
                  <Menu>
                    <Menu.Item
                      onClick={() => setFilters({ ...filters, assignee: null })}
                    >
                      Clear Filters
                    </Menu.Item>
                    <Menu.Divider />
                    {users.map((u) => (
                      <Menu.Item
                        key={u.id}
                        onClick={() =>
                          setFilters({ ...filters, assignee: u.id })
                        }
                      >
                        {u.firstname} {u.lastname}
                      </Menu.Item>
                    ))}
                  </Menu>
                }
              >
                <Button icon={<FilterOutlined />}>Filter by Assignee</Button>
              </Dropdown>
            </Space>
            {selectedRowKeys.length > 0 && (
              <Space>
                <span>{selectedRowKeys.length} selected</span>
                <Dropdown
                  overlay={
                    <Menu>
                      <Menu.Item onClick={() => bulkUpdate("status", "done")}>
                        Mark as Done
                      </Menu.Item>
                      <Menu.Item onClick={() => bulkUpdate("status", "todo")}>
                        Mark as To Do
                      </Menu.Item>
                      <Menu.Divider />
                      <Menu.Item onClick={() => bulkUpdate("priority", "high")}>
                        Set Priority High
                      </Menu.Item>
                      <Menu.Item onClick={() => bulkUpdate("priority", "low")}>
                        Set Priority Low
                      </Menu.Item>
                    </Menu>
                  }
                >
                  <Button>Bulk Update</Button>
                </Dropdown>
                <Popconfirm
                  title="Delete selected tasks?"
                  onConfirm={() => bulkUpdate("delete", null)}
                >
                  <Button danger>Delete</Button>
                </Popconfirm>
              </Space>
            )}
          </div>
          <Divider />
          <Table
            size="small"
            rowKey="id"
            rowSelection={rowSelection}
            columns={columns}
            dataSource={tasks}
            loading={loading}
            pagination={{ pageSize: 20 }}
          />
        </TabPane>

        <TabPane tab="Board" key="board">
          <TaskBoard />
        </TabPane>

        <TabPane tab="Calendar" key="calendar">
          <FullCalendar
            key={calendarKey}
            plugins={[
              dayGridPlugin,
              timeGridPlugin,
              listPlugin,
              interactionPlugin,
            ]}
            initialView="dayGridMonth"
            events={calendarEvents}
            eventClick={(info) => {
              const { type, data } = info.event.extendedProps;
              if (type === "task") {
                setSelectedTaskId(data.id);
                setDrawerVisible(true);
              } else if (type === "event")
                Modal.info({ title: data.title, content: data.description });
            }}
            eventDrop={(info) => {
              const taskId = info.event.id.split("_")[1];
              const newDate = info.event.start.toISOString();
              updateTask(taskId, { due_date: newDate });
            }}
            dateClick={(info) => {
              taskForm.setFieldsValue({ due_date: dayjs(info.date) });
              setTaskModalVisible(true);
            }}
            height="auto"
          />
        </TabPane>
      </Tabs>

      {/* Task Modal */}
      <Modal
        title={editingTask ? "Edit Task" : "New Task"}
        open={taskModalVisible}
        onCancel={() => {
          setTaskModalVisible(false);
          taskForm.resetFields();
          setEditingTask(null);
        }}
        footer={null}
        width={600}
      >
        <Form form={taskForm} layout="vertical" onFinish={handleTaskSubmit}>
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={3} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="status" label="Status" initialValue="todo">
                <Select>
                  <Option value="todo">To Do</Option>
                  <Option value="in_progress">In Progress</Option>
                  <Option value="review">Review</Option>
                  <Option value="done">Done</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="priority" label="Priority" initialValue="medium">
                <Select>
                  <Option value="low">Low</Option>
                  <Option value="medium">Medium</Option>
                  <Option value="high">High</Option>
                  <Option value="urgent">Urgent</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="due_date" label="Due Date">
                <DatePicker
                  showTime
                  format="YYYY-MM-DD HH:mm:ss"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="start_date" label="Start Date">
                <DatePicker
                  showTime
                  format="YYYY-MM-DD HH:mm:ss"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="assigned_to" label="Assign To">
            <Select
              options={users.map((u) => ({
                label: `${u.firstname} ${u.lastname}`,
                value: u.id,
              }))}
            />
          </Form.Item>
          <Form.Item name="estimated_hours" label="Est. Hours">
            <InputNumber min={0} step={0.5} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="tags" label="Tags">
            <Select mode="tags" placeholder="Add tags" />
          </Form.Item>
          <Form.Item
            name="reminder_minutes"
            label="Reminder (minutes before due)"
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                Save
              </Button>
              <Button onClick={() => setTaskModalVisible(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Event Modal */}
      <Modal
        title={editingEvent ? "Edit Event" : "New Event"}
        open={eventModalVisible}
        onCancel={() => {
          setEventModalVisible(false);
          eventForm.resetFields();
          setEditingEvent(null);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={eventForm}
          layout="vertical"
          onFinish={async (values) => {
            setLoading(true);
            try {
              const payload = {
                ...values,
                start_date: values.date_range[0].format("YYYY-MM-DD HH:mm:ss"),
                end_date: values.date_range[1].format("YYYY-MM-DD HH:mm:ss"),
                created_by: account.user.id,
              };
              if (editingEvent)
                await api.put(
                  `/api/events/${editingEvent.id}`,
                  payload,
                );
              else await api.post(`/api/events`, payload);
              message.success("Event saved");
              setEventModalVisible(false);
              eventForm.resetFields();
              setEditingEvent(null);
              fetchEvents();
            } catch (err) {
              message.error("Failed to save event");
            } finally {
              setLoading(false);
            }
          }}
        >
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item name="location" label="Location">
            <Input />
          </Form.Item>
          <Form.Item
            name="date_range"
            label="Date & Time"
            rules={[{ required: true }]}
          >
            <RangePicker showTime format="YYYY-MM-DD HH:mm:ss" />
          </Form.Item>
          <Form.Item name="all_day" label="All day" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="color" label="Color">
            <Input type="color" />
          </Form.Item>
          <Form.Item name="reminder_minutes" label="Reminder (minutes before)">
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                Save
              </Button>
              <Button onClick={() => setEventModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Custom Status Modal */}
      <Modal
        title="Add Custom Status Column"
        open={addStatusModalVisible}
        onCancel={() => setAddStatusModalVisible(false)}
        footer={null}
      >
        <Form form={newStatusForm} onFinish={addCustomStatus} layout="vertical">
          <Form.Item
            name="name"
            label="Status Name"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="color" label="Color" initialValue="#1890ff">
            <Input type="color" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Add Status
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Task Details Drawer */}
      <TaskDetailsDrawer
        visible={drawerVisible}
        taskId={selectedTaskId}
        onClose={() => setDrawerVisible(false)}
        users={users}
        apiUrl={apiUrl}
        userId={account.user.id}
        onRefresh={fetchTasks}
      />
    </Card>
  );
};

export default CalendarManagement;
